import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Swiss Chem International',
  
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
  email: 'ceo@swisschem.pk',
  website: 'www.swisschem.pk',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtMoney = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
const fmtNum = (n) => { const v = parseFloat(n) || 0; return v % 1 === 0 ? v.toLocaleString('en-PK') : v.toFixed(2); };
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');

// Shared header + returns the y position after header
const drawHeader = (doc, title, filterText) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;
  let y = 16;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(231, 76, 60);
  doc.text(COMPANY.name, marginX, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  

  const rightX = pageWidth - marginX;
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(COMPANY.address, rightX, y, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}`, rightX, y + 4, { align: 'right' });
  doc.text(COMPANY.email, rightX, y + 8, { align: 'right' });
  doc.text(COMPANY.website, rightX, y + 12, { align: 'right' });

  y += 18;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.4);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 9;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(44, 62, 80);
  doc.text(title, pageWidth / 2, y, { align: 'center' });
  y += 7;

  if (filterText) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    doc.text(filterText, pageWidth / 2, y, { align: 'center' });
    y += 8;
  } else {
    y += 2;
  }
  return y;
};

const drawFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(7.5);
  doc.setTextColor(160, 160, 160);
  doc.text(`Generated on ${new Date().toLocaleString('en-GB')} · ${COMPANY.website}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
};

const periodText = (f) => {
  const range = `${f.from_date ? fmtDate(f.from_date) : 'Start'} to ${f.to_date ? fmtDate(f.to_date) : 'Today'}`;
  let t = `Period: ${range}`;
  if (f.customer_name) t += `   |   Customer: ${f.customer_name}`;
  if (f.vendor_name) t += `   |   Vendor: ${f.vendor_name}`;
  if (f.payment_status && f.payment_status !== 'all') t += `   |   Status: ${titleCase(f.payment_status)}`;
  return t;
};

const headFootStyles = {
  theme: 'grid',
  headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
  bodyStyles: { fontSize: 8, textColor: [44, 62, 80] },
  footStyles: { fillColor: [236, 240, 241], textColor: [44, 62, 80], fontStyle: 'bold', fontSize: 8 },
  margin: { left: 14, right: 14 },
};

// ─── SALES ─────────────────────────────────────────────────
export const generateSalesReportPdf = (report, filters = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const y = drawHeader(doc, 'SALES REPORT', periodText(filters));
  const t = report.totals || {};
  const rows = (report.rows || []).map((r, i) => [
    i + 1, r.invoice_no, fmtDate(r.invoice_date), r.customer_name, r.items_summary || '—',
    fmtMoney(r.subtotal), fmtMoney(r.gst_amount), fmtMoney(r.grand_total), fmtMoney(r.amount_paid), fmtMoney(r.balance), titleCase(r.payment_status),
  ]);
  autoTable(doc, {
    startY: y,
    head: [['#', 'Invoice', 'Date', 'Customer', 'Items', 'Subtotal', 'GST', 'Total', 'Paid', 'Balance', 'Status']],
    body: rows.length ? rows : [['—','—','—','—','No sales','—','—','—','—','—','—']],
    foot: rows.length ? [['', '', '', '', 'TOTALS', '', fmtMoney(t.total_gst), fmtMoney(t.total_sales), fmtMoney(t.total_paid), fmtMoney(t.total_outstanding), `${t.invoice_count} inv`]] : undefined,
    columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' } },
    ...headFootStyles,
  });
  drawFooter(doc);
  doc.save(`Sales_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// ─── PURCHASE ──────────────────────────────────────────────
export const generatePurchaseReportPdf = (report, filters = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const y = drawHeader(doc, 'PURCHASE REPORT', periodText(filters));
  const t = report.totals || {};
  const rows = (report.rows || []).map((r, i) => [
    i + 1, r.bill_no, fmtDate(r.purchase_date), r.vendor_name, r.items_summary || '—',
    fmtMoney(r.subtotal), fmtMoney(r.gst_amount), fmtMoney(r.grand_total), fmtMoney(r.amount_paid), fmtMoney(r.balance), titleCase(r.payment_status),
  ]);
  autoTable(doc, {
    startY: y,
    head: [['#', 'Bill', 'Date', 'Vendor', 'Items', 'Subtotal', 'GST', 'Total', 'Paid', 'Balance', 'Status']],
    body: rows.length ? rows : [['—','—','—','—','No purchases','—','—','—','—','—','—']],
    foot: rows.length ? [['', '', '', '', 'TOTALS', '', fmtMoney(t.total_gst), fmtMoney(t.total_purchases), fmtMoney(t.total_paid), fmtMoney(t.total_outstanding), `${t.bill_count} bills`]] : undefined,
    columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' } },
    ...headFootStyles,
  });
  drawFooter(doc);
  doc.save(`Purchase_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// ─── STOCK ─────────────────────────────────────────────────
export const generateStockReportPdf = (report, filters = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const y = drawHeader(doc, 'STOCK VALUATION REPORT', `As of ${fmtDate(new Date())}`);
  const t = report.totals || {};
  const rows = (report.rows || []).map((r, i) => [
    i + 1, r.product_name, r.product_code, titleCase(r.product_type), r.batch_no,
    `${fmtNum(r.qty_available)} ${r.product_unit}`, fmtMoney(r.cost_per_unit), fmtMoney(r.stock_value), fmtDate(r.expiry_date),
  ]);
  autoTable(doc, {
    startY: y,
    head: [['#', 'Product', 'Code', 'Type', 'Batch', 'Qty', 'Cost/Unit', 'Value', 'Expiry']],
    body: rows.length ? rows : [['—','—','—','—','No stock','—','—','—','—']],
    foot: rows.length ? [['', '', '', '', '', '', 'TOTAL VALUE', fmtMoney(t.total_value), `${t.batch_count} batches`]] : undefined,
    columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
    ...headFootStyles,
  });
  drawFooter(doc);
  doc.save(`Stock_Valuation_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// ─── PRODUCTION ────────────────────────────────────────────
export const generateProductionReportPdf = (report, filters = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const y = drawHeader(doc, 'PRODUCTION REPORT', periodText(filters));
  const t = report.totals || {};
  const rows = (report.rows || []).map((r, i) => [
    i + 1, r.batch_no, fmtDate(r.production_date), r.product_name, r.formula_code,
    `${parseFloat(r.multiplier).toFixed(1)}x`, `${fmtNum(r.expected_output)} ${r.product_unit}`, `${fmtNum(r.actual_output)} ${r.product_unit}`, fmtMoney(r.total_cost),
  ]);
  autoTable(doc, {
    startY: y,
    head: [['#', 'Batch', 'Date', 'Product', 'Recipe', 'Mult', 'Expected', 'Actual', 'Cost']],
    body: rows.length ? rows : [['—','—','—','—','No productions','—','—','—','—']],
    foot: rows.length ? [['', '', '', '', '', 'TOTALS', '', fmtNum(t.total_output), fmtMoney(t.total_cost)]] : undefined,
    columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' } },
    ...headFootStyles,
  });
  drawFooter(doc);
  doc.save(`Production_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// ─── LEDGER ────────────────────────────────────────────────
export const generateLedgerReportPdf = (report, filters = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }); // portrait
  const party = report.party || {};
  const ptype = filters.party_type === 'vendor' ? 'Vendor' : 'Customer';
  const y = drawHeader(doc, 'LEDGER STATEMENT', `${ptype}: ${party.name || '—'}   |   ${periodText(filters)}`);
  const t = report.totals || {};
  const rows = (report.rows || []).map((r) => {
    const parts = [];
    if (r.kind === 'sale' || r.kind === 'invoice')      parts.push(`Invoice #${r.against_no || r.ref}`);
    else if (r.kind === 'purchase' || r.kind === 'bill') parts.push(`Purchase Bill #${r.against_no || r.ref}`);
    else if (r.kind === 'receipt')                       parts.push(`Receipt ${r.ref}${r.against_no ? ' against Invoice #' + r.against_no : ''}`);
    else if (r.kind === 'payment')                       parts.push(`Payment ${r.ref}${r.against_no ? ' against Bill #' + r.against_no : ''}`);
    else                                                 parts.push(r.against_no || r.ref || '—');
    if (r.items_summary) parts.push(r.items_summary);
    if (r.notes) parts.push(r.notes);
    return [
      fmtDate(r.date),
      parts.join(' — '),
      titleCase(r.kind),
      r.charge ? fmtMoney(r.charge) : '—',
      r.paid ? fmtMoney(r.paid) : '—',
      fmtMoney(r.balance),
    ];
  });
  autoTable(doc, {
    startY: y,
    head: [['Date', 'Narration', 'Type', 'Debit', 'Credit', 'Balance']],
    body: rows.length ? rows : [['—','No transactions','—','—','—','—']],
    foot: rows.length ? [['', '', 'TOTALS', fmtMoney(t.total_charge), fmtMoney(t.total_paid), fmtMoney(t.closing_balance)]] : undefined,
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 22 },
      3: { cellWidth: 26, halign: 'right' },
      4: { cellWidth: 26, halign: 'right' },
      5: { cellWidth: 30, halign: 'right' },
    },
    ...headFootStyles,
  });

  drawFooter(doc);
  doc.save(`Ledger_${(party.name || 'party').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// ─── PROFIT & LOSS ─────────────────────────────────────────
export const generateProfitLossReportPdf = (report, filters = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }); // portrait
  const s = report.summary || {};
  let y = drawHeader(doc, 'PROFIT & LOSS STATEMENT', periodText(filters));

  // Main P&L summary table
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: [
      ['Sales Revenue', fmtMoney(s.sales_revenue)],
      ['Less: Purchase Cost', `(${fmtMoney(s.purchase_cost)})`],
      ['Less: Total Expenses', `(${fmtMoney(s.total_expenses)})`],
    ],
    foot: [['NET PROFIT / (LOSS)', fmtMoney(s.net_profit)]],
    theme: 'grid',
    headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 10, textColor: [44, 62, 80] },
    footStyles: {
      fillColor: s.net_profit >= 0 ? [212, 237, 218] : [248, 215, 218],
      textColor: s.net_profit >= 0 ? [21, 87, 36] : [114, 28, 36],
      fontStyle: 'bold', fontSize: 11,
    },
    columnStyles: { 1: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });

  // Expense breakdown
  if (report.expense_breakdown && report.expense_breakdown.length > 0) {
    let by = doc.lastAutoTable.finalY + 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(44, 62, 80);
    doc.text('Expense Breakdown', 14, by);
    autoTable(doc, {
      startY: by + 3,
      head: [['Category', 'Amount']],
      body: report.expense_breakdown.map((e) => [e.category, fmtMoney(e.amount)]),
      theme: 'grid',
      headStyles: { fillColor: [142, 68, 173], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [44, 62, 80] },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
  }

  drawFooter(doc);
  doc.save(`Profit_Loss_${new Date().toISOString().slice(0, 10)}.pdf`);
};